import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PriorityBadge } from '../components/common/PriorityBadge';
import { StatusBadge } from '../components/common/StatusBadge';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { getCalendarEvents } from '../lib/api';
import { formatDate } from '../lib/utils';
import { CalendarIcon, Wrench, CalendarClock, FileWarning } from 'lucide-react';
import { toast } from 'sonner';

export default function CalendarPage() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedEvents, setSelectedEvents] = useState([]);

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const response = await getCalendarEvents();
            setEvents(response.data);
        } catch (error) {
            console.error('Failed to fetch calendar events:', error);
            toast.error('Gagal memuat event');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedDate) {
            const dateStr = selectedDate.toISOString().split('T')[0];
            const dayEvents = events.filter(event => event.date === dateStr);
            setSelectedEvents(dayEvents);
        }
    }, [selectedDate, events]);

    const getEventDates = () => {
        const dates = {};
        events.forEach(event => {
            if (event.date) {
                if (!dates[event.date]) {
                    dates[event.date] = [];
                }
                dates[event.date].push(event);
            }
        });
        return dates;
    };

    const eventDates = getEventDates();

    const modifiers = {
        hasEvents: (date) => {
            const dateStr = date.toISOString().split('T')[0];
            return !!eventDates[dateStr];
        }
    };

    const getEventIcon = (type) => {
        switch (type) {
            case 'schedule':
                return <CalendarClock className="w-4 h-4 text-primary" />;
            case 'work_order':
                return <Wrench className="w-4 h-4 text-blue-500" />;
            case 'document':
                return <FileWarning className="w-4 h-4 text-orange-500" />;
            default:
                return <CalendarIcon className="w-4 h-4 text-muted-foreground" />;
        }
    };

    if (loading) {
        return (
            <Layout title="Kalender">
                <LoadingSpinner size="large" />
            </Layout>
        );
    }

    return (
        <Layout title="Kalender">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="calendar-page">
                {/* Calendar */}
                <Card className="industrial-card lg:col-span-2">
                    <CardHeader className="border-b border-border pb-4">
                        <CardTitle className="section-title flex items-center gap-2">
                            <CalendarIcon className="w-5 h-5 text-primary" />
                            Kalender Maintenance
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="flex justify-center">
                            <Calendar
                                mode="single"
                                selected={selectedDate}
                                onSelect={setSelectedDate}
                                modifiers={modifiers}
                                className="rounded-sm border"
                                components={{
                                    DayContent: ({ date }) => {
                                        const dateStr = date.toISOString().split('T')[0];
                                        const dayEvents = eventDates[dateStr] || [];
                                        const hasSchedule = dayEvents.some(e => e.type === 'schedule');
                                        const hasWorkOrder = dayEvents.some(e => e.type === 'work_order');
                                        const hasDocument = dayEvents.some(e => e.type === 'document');
                                        
                                        return (
                                            <div className="relative w-full h-full flex flex-col items-center justify-center">
                                                <span>{date.getDate()}</span>
                                                {dayEvents.length > 0 && (
                                                    <div className="flex gap-0.5 mt-0.5">
                                                        {hasSchedule && (
                                                            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                                        )}
                                                        {hasWorkOrder && (
                                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                                        )}
                                                        {hasDocument && (
                                                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                }}
                            />
                        </div>
                        
                        <div className="flex justify-center gap-6 mt-6">
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-primary" />
                                <span className="text-xs text-muted-foreground">Jadwal Service</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-blue-500" />
                                <span className="text-xs text-muted-foreground">Work Order</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-orange-500" />
                                <span className="text-xs text-muted-foreground">Dokumen Expired</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Selected Date Events */}
                <Card className="industrial-card">
                    <CardHeader className="border-b border-border pb-4">
                        <CardTitle className="section-title text-lg">
                            {selectedDate ? formatDate(selectedDate.toISOString()) : 'Pilih Tanggal'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        {selectedEvents.length > 0 ? (
                            <div className="space-y-3">
                                {selectedEvents.map((event, index) => (
                                    <div 
                                        key={event.id || index}
                                        className="p-3 bg-muted/30 rounded-sm border border-border hover:border-primary/50 transition-colors"
                                        data-testid={`calendar-event-${index}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${
                                                event.type === 'schedule' ? 'bg-primary/10' : 
                                                event.type === 'work_order' ? 'bg-blue-500/10' : 'bg-orange-500/10'
                                            }`}>
                                                {getEventIcon(event.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm truncate">{event.title}</p>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {event.vehicle || 'No vehicle'}
                                                </p>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <PriorityBadge priority={event.priority} />
                                                    {event.status && <StatusBadge status={event.status} />}
                                                </div>
                                                {event.wo_number && (
                                                    <p className="text-xs font-mono text-muted-foreground mt-1">
                                                        {event.wo_number}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <CalendarIcon className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                                <p className="text-sm text-muted-foreground">
                                    Tidak ada event di tanggal ini
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Upcoming Events Summary */}
                <Card className="industrial-card lg:col-span-3">
                    <CardHeader className="border-b border-border pb-4">
                        <CardTitle className="section-title text-lg">Event Mendatang</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {events
                                .filter(e => new Date(e.date) >= new Date())
                                .sort((a, b) => new Date(a.date) - new Date(b.date))
                                .slice(0, 8)
                                .map((event, index) => (
                                    <div 
                                        key={event.id || index}
                                        className="p-4 bg-muted/30 rounded-sm border border-border hover:border-primary/50 transition-colors"
                                        data-testid={`upcoming-event-${index}`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className={`text-xs font-mono uppercase tracking-wider ${
                                                event.type === 'schedule' ? 'text-primary' : 
                                                event.type === 'work_order' ? 'text-blue-500' : 'text-orange-500'
                                            }`}>
                                                {event.type === 'schedule' ? 'JADWAL' : event.type === 'work_order' ? 'WORK ORDER' : 'DOKUMEN'}
                                            </span>
                                            <PriorityBadge priority={event.priority} />
                                        </div>
                                        <p className="font-medium text-sm truncate mb-1">{event.title}</p>
                                        <p className="text-xs text-muted-foreground truncate">{event.vehicle}</p>
                                        <p className="text-xs text-muted-foreground mt-2">{formatDate(event.date)}</p>
                                    </div>
                                ))}
                        </div>
                        {events.filter(e => new Date(e.date) >= new Date()).length === 0 && (
                            <p className="text-center text-sm text-muted-foreground py-8">
                                Tidak ada event mendatang
                            </p>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
